import type { EntityType, FlatContentLayersType } from "@config";
import { nanoid } from "@reduxjs/toolkit";
import cloneDeep from "lodash/cloneDeep";

type TableDataCell = { id: string; mergedInto?: string };

export function processNewEntity({
  entityEntryRootId,
  entities,
  parentId,
}: {
  entityEntryRootId: string;
  entities: Record<string, EntityType>;
  parentId: string | null;
}): {
  root: EntityType | undefined;
  entities: Record<string, EntityType>;
} {
  const rootEntity = entities[entityEntryRootId];
  const updated = {} as Record<string, EntityType>;
  // Old→new tableData cell ids, filled when a cloned entity carries
  // tableData (root-first, so descendants' cellId can be remapped).
  const cellIdMap = new Map<string, string>();
  const newEntity = createEntity(rootEntity, parentId, updated, cellIdMap);
  if (newEntity) handleChildIds(newEntity, entities, updated, cellIdMap);
  return { root: newEntity || undefined, entities: updated };
}

const updateIdsArray = (
  idsArray: string[],
  oldId: string,
  newId: string,
): void => {
  const index = idsArray.indexOf(oldId);
  idsArray[index] = newId;
};

// Fresh cell ids for a cloned table so two tables never share cell ids
// (cross-table collisions corrupt collab + per-cell features); mergedInto
// chains are remapped in a second pass once every new id exists.
const regenerateTableCellIds = (
  entity: EntityType,
  cellIdMap: Map<string, string>,
): void => {
  const tableData = entity.settings?.tableData as TableDataCell[] | undefined;
  if (!Array.isArray(tableData)) return;
  for (const cell of tableData) {
    if (!cell?.id) continue;
    const newId = nanoid();
    cellIdMap.set(cell.id, newId);
    cell.id = newId;
  }
  for (const cell of tableData) {
    if (cell?.mergedInto) {
      cell.mergedInto = cellIdMap.get(cell.mergedInto) ?? cell.mergedInto;
    }
  }
};

const createEntity = (
  entity: EntityType,
  parentId: string | null,
  updated: Record<string, EntityType>,
  cellIdMap: Map<string, string>,
): EntityType | undefined => {
  const parent = updated[parentId || ""];
  const newEntity: EntityType = cloneDeep({
    ...entity,
    id: nanoid(15),
    parentId,
  });
  regenerateTableCellIds(newEntity, cellIdMap);
  const cellId = newEntity.settings?.cellId as string | undefined;
  const remapped = cellId ? cellIdMap.get(cellId) : undefined;
  if (remapped) newEntity.settings.cellId = remapped;
  const hasParent = parent && parentId !== null;
  if (hasParent) updateIdsArray(parent.childIds, entity.id, newEntity.id);
  updated[newEntity.id] = newEntity;
  return newEntity;
};

const handleChildIds = (
  entity: EntityType,
  entities: FlatContentLayersType,
  updated: Record<string, EntityType>,
  cellIdMap: Map<string, string>,
): void => {
  entity.childIds.forEach((childId) => {
    const child = entities[childId];
    if (!child) return;
    const newChild = createEntity(child, entity.id, updated, cellIdMap);
    if (newChild) handleChildIds(newChild, entities, updated, cellIdMap);
  });
};
